import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import type {Broadcast} from './contexts/Configuration';
import {BROADCAST_SERVER_URL, BroadcastResponse} from './contexts/Configuration';

import TranslationHistoryCast from './components/TranslationtHistoryCast';
import {FormControl, InputLabel, MenuItem, Select, SelectChangeEvent} from '@mui/material';

import './ConsumerApp.scss';
import logo_white from './logo_white.png';
import Alert from '@mui/material/Alert';
import {RosettaClient} from './api/RosettaClient';

const WARN_NO_ACTIVE_SESSION = 'NO_ACTIVE_SESSIONS';
const WARN_BROADCAST_IS_NOT_ENABLED = 'BROADCAST_IS_NOT_ENABLE';
const ERROR_INVALID_SHARE_CODE = 'INVALID_SHARE_CODE';

let timeoutRef: NodeJS.Timeout;

const capitalizeFirstLetter = (s: string) => {
    return s.charAt(0).toUpperCase() + s.slice(1);
}

const fetchTranslations = async (params: { userCode: string, shareCode: string, startPollingTimestamp: number }) => {
    return await fetch(BROADCAST_SERVER_URL + `broadcast/${params.userCode}/translations?shareCode=${params.shareCode}&transcriptionsFrom=${params.startPollingTimestamp}`, {
        method: 'GET',
        headers: {
            'content-type': 'application/json'
        }
    })
        .then(response => RosettaClient.marshalBroadcastResponse(response as BroadcastResponse))
}

const showAlertIfSessionNotAvailable = (sessionState: 'WAITING_FOR_SESSION' | 'SESSION_IN_PROGRESS' | 'SESSION_FINISHED', displayLanguage: string | null) => {
    switch (sessionState) {
        case 'WAITING_FOR_SESSION':
            return (
                <Alert severity={'info'}>There is no session currently in progress. Please wait for the next session to
                    begin.</Alert>);

        case 'SESSION_IN_PROGRESS':
            return displayLanguage ? (<></>)
                : (<Alert severity={'info'} className="special">
                        Session currently in progress. Please select the desired language.
                    </Alert>
                );

        case 'SESSION_FINISHED':
            return (<Alert severity={'info'}>Session has ended. Please wait for the next session to begin.</Alert>);

        default:
            return <></>;
    }
}

const ConsumerApp: FC<ConsumerAppProps> = ({userCode, shareCode}) => {

    const [loading, setLoading] = useState<boolean>(true);
    const [showErrorScreen, setShowErrorScreen] = useState<boolean>(false); //FOR INVALID SHARECODE

    const [isBroadcastEnable, setIsBroadcastEnable] = useState<boolean>(false);
    const [sessionState, setSessionState] = useState<'WAITING_FOR_SESSION' | 'SESSION_IN_PROGRESS' | 'SESSION_FINISHED'>('WAITING_FOR_SESSION');

    //Translation selection (of selected session)
    const [translationsOfSession, setTranslationsOfSession] = useState<{ [key: string]: string[] }>({});
    const [availableLanguagesOfSession, setAvailableLanguagesOfSession] = useState<{ id: string, name: string }[]>([]);
    const [displayLanguage, setDisplayLanguage] = useState<string>('');

    // const dispatch = useAppDispatch();
    const startPollingTimestamp = useRef<number>(0);
    const isBroadcastEnableRef = useRef<boolean>(isBroadcastEnable);
    const sessionStateRef = useRef<string>(sessionState);

    const sessionEndTimestamp = useRef<number>(0);

    //Update references should always be here
    useEffect(() => {
        isBroadcastEnableRef.current = isBroadcastEnable
        sessionStateRef.current = sessionState
    }, [isBroadcastEnable, sessionState]);

    useEffect(() => {
        if (!userCode) {
            setShowErrorScreen(true);
            setLoading(false);
            return;
        }

        startPollingTimestamp.current = Date.now();

        fetchTranslations({userCode, shareCode, startPollingTimestamp: startPollingTimestamp.current})
            .then(response => {
                setLoading(false);

                const errorResponse = updateState(response);
                if (ERROR_INVALID_SHARE_CODE === errorResponse) return;

                timeoutRef = setInterval(() => fetchTranslations({
                    userCode,
                    shareCode,
                    startPollingTimestamp: startPollingTimestamp.current
                })
                    .then(response => updateState(response)), 3500);
            })

    }, [userCode]);

    const updateState = (response: Broadcast) => {
        if (!response.error) {
            const selectedSessionId = Object.keys(response.translations)[0] || '';
            const translations = response.translations[selectedSessionId] || {};

            setTranslationsOfSession(translations);
            setAvailableLanguagesOfSession(Object.keys(translations).map(k => ({id: k, name: k})))

            if (sessionStateRef.current === 'WAITING_FOR_SESSION' || sessionStateRef.current === 'SESSION_FINISHED') {
                setSessionState('SESSION_IN_PROGRESS');
                sessionEndTimestamp.current = 0;
            }
            return 'OK';
        }

        //Handle error
        switch (response.error) {
            case ERROR_INVALID_SHARE_CODE:
                //stop polling if active - show error screen
                setShowErrorScreen(true);

                if (timeoutRef) {
                    clearTimeout(timeoutRef);
                }
                return response.error;
            //------------------------------------------------
            case WARN_BROADCAST_IS_NOT_ENABLED: //keep polling
                if (isBroadcastEnableRef.current) {
                    //console.log('==> [updateState] isBroadcastEnable', isBroadcastEnableRef.current, isBroadcastEnable)
                    setIsBroadcastEnable(false);
                }
                return 'OK';
            //------------------------------------------------
            case WARN_NO_ACTIVE_SESSION:
                console.log('Session has finished - [0] Starting to check sessionStateRef', sessionStateRef.current)

                switch (sessionStateRef.current) {
                    case 'WAITING_FOR_SESSION':
                        //initial state - keep waiting
                        break;
                    case 'SESSION_IN_PROGRESS':
                        //session has ended - clean up
                        setSessionState('SESSION_FINISHED');
                        setDisplayLanguage('')
                        setTranslationsOfSession({});
                        setAvailableLanguagesOfSession([]);

                        break;
                    case 'SESSION_FINISHED':
                        if (sessionEndTimestamp.current === 0) {
                            console.log('Session has finished - [1] setting sessionEndTimestamp')
                            sessionEndTimestamp.current = Date.now();

                        } else {
                            console.log('Session has finished - [2] checking sessionEndTimestamp')
                            const nowTimestamp = Date.now();
                            const sessionEndTimestampPlus10 = sessionEndTimestamp.current + (8 * 1000);
                            const _10SecFlag = sessionEndTimestamp.current > 0 && nowTimestamp > sessionEndTimestampPlus10;

                            // console.log(`Comparing [${sessionEndTimestamp.current} > 0 && ${nowTimestamp} > ${sessionEndTimestampPlus10}]:`, _10SecFlag);
                            if (_10SecFlag) {
                                console.log('Session has finished - [3] 10sec passed! ')
                                sessionEndTimestamp.current = 0;
                                setSessionState('WAITING_FOR_SESSION');
                            }
                        }
                        break;
                }
                return 'OK';
            //------------------------------------------------
        }
    }

    const languageOptions = useMemo(() => {
        // console.log('==> [languageOptions] ???', availableLanguagesOfSession)
        return availableLanguagesOfSession.map(op => {
                const displayNames = new Intl.DisplayNames([op.id], {type: 'language', languageDisplay: 'standard'});
                return (
                    <MenuItem value={op.id}>
                        {capitalizeFirstLetter(displayNames.of(op.id) || op.id)}
                    </MenuItem>)
            }
        );
    }, [availableLanguagesOfSession]);

    if (loading) {
        return (
            <div className="loading-screen">
                <img src={logo_white} alt="Company Logo" className="logo"/>
            </div>
        );
    }

    if (showErrorScreen) {
        return (
            <div className="loading-screen">
                <img src={logo_white} alt="Company Logo" className="logo"/>
            </div>
        );
    }

    return (
        <div className="cast-container">
            <div className="cast-header">
                <div className="cast-header-decoration">
                    <div className="cast-header-decoration-top">
                        <img src={logo_white} alt="Company Logo" className="cast-header-logo"/>
                        <span className="cast-header-welcome">Welcome to our interpretation service for Enersound clients</span>
                    </div>
                    <div className="cast-header-selector">
                        <FormControl variant="filled" sx={{m: 1, minWidth: 120}}
                                     disabled={availableLanguagesOfSession.length <= 0}
                        >
                            <InputLabel id="select-lang">
                                {availableLanguagesOfSession.length === 0 ? 'Loading translations' : 'Please select a language'}
                            </InputLabel>
                            <Select
                                id="selectableLanguageOptions"
                                labelId="select-lang"
                                disableUnderline
                                label={availableLanguagesOfSession.length === 0 ? 'Loading translations' : 'Please select a language'}
                                value={displayLanguage}
                                onChange={(event: SelectChangeEvent) => setDisplayLanguage(event.target.value as string || '')}
                            >
                                {languageOptions}
                            </Select>
                        </FormControl>
                    </div>
                </div>
            </div>
            <div className="cast-body">

                <div id="cast_history_container" className="cast-history-container">
                    {showAlertIfSessionNotAvailable(sessionState, displayLanguage)}

                    <TranslationHistoryCast id="cast"
                                            translations={displayLanguage && displayLanguage !== '' ? translationsOfSession[displayLanguage] : []}
                    />
                </div>
            </div>
            <div className="cast-footer">
                <span className="cast-footer-slogan">Passion for innovation</span>
                <a className="cast-footer-link" target="_blank" href="https://www.enersound.com">www.enersound.com</a>
            </div>
        </div>
    );

}

type ConsumerAppProps = {
    userCode: string,
    shareCode: string
}

export default ConsumerApp;
